import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { useFromNumbers } from 'v2/hooks/use-from-numbers'
import { ConvChannels } from 'v2/types/api/conversations'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { NodevarList } from '../components/nodevar-list'
import { initiateCallEventTypes } from '../config/initiate-call.config'
import { v2validations } from '../config/validation.config'
import type { InitiateCallNodeData, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = InitiateCallNodeData['config']

const fields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).initiate_call
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
    },
    {
      name: 'from',
      label: 'From',
      zodValidation: validations.shape.from,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter from number',
        endIcon: (
          <NodevarList
            nodevars={fieldsWithDynamicNodeVars.from}
            className="pr-2"
            inputElementSelector="[name=from]"
          />
        ),
      },
    },
    {
      name: 'to',
      label: 'To',
      zodValidation: validations.shape.to,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter to number',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=to]" />
        ),
      },
    },
    {
      name: 'is_validate_caller_id',
      label: 'Validate caller ID',
      componentType: 'switch',
      zodValidation: validations.shape.is_validate_caller_id,
      inputProps: {
        description: 'When enabled, the from number is validated as a caller ID before dialing.',
      },
    },
    {
      name: 'extra_headers',
      label: 'Custom Headers',
      zodValidation: validations.shape.extra_headers,
      componentType: 'textarea',
      inputProps: {
        placeholder: 'X-PH-Key=value',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=extra_headers]"
          />
        ),
      },
    },
    {
      name: 'send_digits',
      label: 'Send as input when call is answered',
      zodValidation: validations.shape.send_digits,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter DTMF digit',
      },
    },
    {
      name: 'ring_timeout',
      label: 'Disconnect after ringing for',
      zodValidation: validations.shape.ring_timeout,
      componentType: 'inputNumber',
      inputProps: {
        min: 1,
        max: 60,
        type: 'number',
      },
    },
    {
      name: 'max_call_duration',
      label: 'Max call duration (minutes)',
      zodValidation: validations.shape.max_call_duration,
      componentType: 'inputNumber',
      inputProps: {
        min: 1,
        max: 240,
        type: 'number',
        placeholder: 'Enter duration in minutes',
        defaultValue: 10,
      },
    },
    // Hide voicemail detection in initiate call node for now
    // {
    //   name: 'detect_voicemail',
    //   label: 'Enable voicemail detection',
    //   zodValidation: validations.shape.detect_voicemail,
    //   componentType: 'checkbox',
    // },
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={initiateCallEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const InitiateCallSidebarComponent = (
  props: SidebarComponentProps<InitiateCallNodeData>,
) => {
  const { nodeVars } = props
  const { accountNumbers } = useFromNumbers(ConvChannels.CALL)
  const fieldsWithDynamicNodeVars = {
    from: [...(accountNumbers?.map((number) => number.number) || []), ...nodeVars],
  }
  const formFields = fields(nodeVars, fieldsWithDynamicNodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
